import { useUpdateSelfServeRequestStatusMutation } from '@api/selfServe';
import {
  DataAccessProvider,
  SelfServeDataAccessStatus,
  TOAST_TYPES,
} from '../../constants/constants';
import { setToastMessage } from '@stores/App/slice';
import { useDispatch } from 'react-redux';
import { UpdateSelfServeRequestStatusParams } from 'src/pages/Admin/SelfServeRequests/ModifyPermissions/types';

const createUpdateSelfServeRequestStatusPayload = ({
  status,
  mmp,
  iapMargin,
  iaaIdList,
  accountName,
  connectionDetails,
  dashboardIds,
  requestedDashboardIds,
}: UpdateSelfServeRequestStatusParams) => {
  switch (status) {
    case SelfServeDataAccessStatus.ACCESS_GRANTED:
      if (mmp === DataAccessProvider.ADJUST || mmp === DataAccessProvider.APPS_FLYER) {
        return {
          status,
          dataSourceDetails: {
            mmp,
            iapMargin,
            iaaIdList,
            accountName,
          },
        };
      } else {
        return {
          status,
          dataSourceDetails: {
            mmp,
            connectionDetails,
          },
        };
      }
    case SelfServeDataAccessStatus.PUBLISHED:
      return {
        status,
        dashboardIds: dashboardIds || [],
        requestedDashboardIds: requestedDashboardIds || [],
      };
    default:
      return {
        status,
      };
  }
};

export const useSelfServeStatusUpdate = () => {
  const [updateSelfServeRequestStatusApi, { isLoading: isUpdatingStatus }] =
    useUpdateSelfServeRequestStatusMutation();
  const dispatch = useDispatch();

  const updateSelfServeRequestStatus = async ({
    id,
    status,
    mmp,
    iapMargin,
    iaaIdList,
    accountName,
    connectionDetails,
    dashboardIds,
    requestedDashboardIds,
  }: UpdateSelfServeRequestStatusParams & { id: string }) => {
    const payload = createUpdateSelfServeRequestStatusPayload({
      status,
      mmp,
      iapMargin,
      iaaIdList,
      accountName,
      connectionDetails,
      dashboardIds,
      requestedDashboardIds,
    });

    let response: any = null;

    try {
      response = await updateSelfServeRequestStatusApi({
        selfServeId: id,
        payload,
      }).unwrap();
    } catch (error: any) {
      if (error?.detailedErrorObject?.code === 4052) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            heading: 'Adjust Account Name Not Found!',
            message:
              "Please ensure that access was granted to underwriting@pvxpartner.com and that your account name exactly matches your Adjust account. If you've already done this, please try syncing again or reach us at info@pvxpartners.com.",
          }),
        );
      } else if (error?.detailedErrorObject?.code === 4053) {
        dispatch(
          setToastMessage({
            type: TOAST_TYPES.ERROR,
            heading: 'Game IDs Not Found!',
            message:
              "The game IDs you shared aren't linked to the Adjust account. Please verify app access.",
          }),
        );
      } else {
        dispatch(
          setToastMessage({
            heading: 'Failed to update Self Serve Request status',
            message: error?.error?.message,
            type: TOAST_TYPES.ERROR,
          }),
        );
      }
    }

    return response;
  };

  return {
    updateSelfServeRequestStatus,
    isUpdatingStatus,
  };
};
